import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useLang } from '../lib/i18n';
import { dayLong, hhmm, tl, TIERS, FEE_KURUS } from '../lib/format';
import { icsFor, saveOrderOffline, useOrder } from '../lib/orders';
import { toast } from '../lib/toast';
import { ErrorView, Loading } from '../components/AppHeader';
import { Ico, Logo, Poster } from '../components/brand';
import { Qr } from '../components/Qr';
import type { OrderOut, TicketOut } from '../lib/types';

const secName = (t: TicketOut, en: boolean) => (en ? (t.sec === 'Parter' ? 'Stalls' : t.sec === 'Balkon' ? 'Balcony' : t.sec) : t.sec);

export function TicketView() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const k = params.get('k');
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const en = lang === 'en';
  const { order: o, error, offline } = useOrder(id, k);
  if (error) return <ErrorView msg={tx('Bilet bulunamadı.', 'Ticket not found.')} action={<button className="btn btn-line" onClick={() => nav('/biletlerim')}>{tx('Biletlerim', 'My tickets')}</button>} />;
  if (!o) return <Loading />;
  if (o.status !== 'paid' || !o.tickets.length) return <ErrorView msg={tx('Bu siparişin geçerli bileti yok.', 'This order has no valid tickets.')} />;
  const i = Math.min(Number(params.get('i') || 0), o.tickets.length - 1);
  const t = o.tickets[i]!;
  const e = o.event;
  const go = (n: number) => {
    const p = new URLSearchParams(params);
    p.set('i', String(n));
    setParams(p, { replace: true });
  };
  const share = async () => {
    const url = `${location.origin}/bilet/${o.id}?k=${o.accessKey ?? k ?? ''}`;
    try {
      if (navigator.share) await navigator.share({ title: `${e.title} — ${o.no}`, url });
      else {
        await navigator.clipboard.writeText(url);
        toast(tx('Bilet bağlantısı kopyalandı', 'Ticket link copied'));
      }
    } catch {
      /* cancelled */
    }
  };
  const loca = !!t.single;
  return (
    <div className="scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 12px' }} className="tk-head">
        <button type="button" className="icon-btn" onClick={() => nav('/biletlerim')} aria-label={tx('Biletlerime dön', 'Back to my tickets')}>
          <Ico n="back" />
        </button>
        <div style={{ flex: 1 }}>
          <div className="d-s">{tx('E-bilet', 'E-ticket')}</div>
          <div className="t-s muted">
            {i + 1} / {o.tickets.length}
            {offline ? tx(' · çevrimdışı kopya', ' · offline copy') : ''}
          </div>
        </div>
        <button type="button" className="icon-btn" onClick={share} aria-label={tx('Bileti paylaş', 'Share ticket')}>
          <Ico n="share" />
        </button>
      </div>
      <div className="tk-wrap" style={{ padding: '0 20px 40px', maxWidth: 420, margin: '0 auto' }}>
        <article className="ticket" aria-label={tx(`Bilet ${i + 1}`, `Ticket ${i + 1}`)}>
          <div className="top">
            <Poster poster={e.poster} title={e.short || e.title} titleSize={e.title.length > 12 ? 34 : 64} />
          </div>
          <div className="body">
            <div className="cap muted">
              {dayLong(o.session.startsAt, lang)} · {hhmm(o.session.startsAt)}
            </div>
            <h1 className="d-m" style={{ margin: '4px 0 2px' }}>
              {e.title}
            </h1>
            <div className="t-s muted">
              {o.venue.name}, {o.venue.area} · {tx('Kapılar', 'Doors')} {e.doors}
            </div>
            <div className="grid">
              <div>
                <div className="k">{loca ? tx('Loca', 'Box') : tx('Bölüm', 'Section')}</div>
                <div className="v">{loca ? String(t.row).replace('Loca ', '') : secName(t, en)}</div>
              </div>
              <div>
                <div className="k">{tx('Sıra', 'Row')}</div>
                <div className="v">{loca ? '—' : t.row}</div>
              </div>
              <div>
                <div className="k">{tx('Koltuk', 'Seat')}</div>
                <div className="v">{t.num}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }} className="t-s">
              <span className="muted">{tx('Giriş', 'Entrance')}</span>
              <b>
                {tx('Kapı', 'Door')} {t.gate}
              </b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }} className="t-s">
              <span className="muted">{tx('Bilet sahibi', 'Ticket holder')}</span>
              <b>{t.holder}</b>
            </div>
          </div>
          <div className="perf" aria-hidden="true" />
          <div className="qrbox">
            <div className="qr">{t.qr ? <Qr text={t.qr} px={188} label={tx('Bilet QR kodu', 'Ticket QR code')} /> : null}</div>
            <span className="code">{t.id}</span>
            {t.usedAt ? (
              <span className="badge">{tx('Kullanıldı', 'Used')}</span>
            ) : (
              <span className="t-s muted" style={{ textAlign: 'center' }}>
                {tx('QR kod imzalıdır ve internet olmadan da okunur. Ekran görüntüsü yerine bu sayfayı kullan.', 'The QR code is signed and scans offline. Use this page rather than a screenshot.')}
              </span>
            )}
          </div>
        </article>
        {o.tickets.length > 1 && (
          <div className="dots" style={{ marginTop: 16 }}>
            {o.tickets.map((_, n) => (
              <button key={n} type="button" aria-label={tx(`Bilet ${n + 1}`, `Ticket ${n + 1}`)} aria-current={n === i} onClick={() => go(n)} />
            ))}
          </div>
        )}
        <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
          <button
            type="button"
            className="btn btn-lg wallet"
            onClick={() => toast(saveOrderOffline(o) ? tx('Bilet bu cihaza kaydedildi · internet olmadan da açılır', 'Ticket saved to this device · opens offline') : tx('Kaydedilemedi', 'Couldn’t save'), 3200)}
          >
            <Ico n="wallet" /> {tx('Cüzdana ekle', 'Add to wallet')}
          </button>
          <div className="grid2">
            <button type="button" className="btn btn-line" onClick={() => nav(`/bilet/${o.id}/pdf${k ? `?k=${k}` : ''}`)}>
              <Ico n="print" cls="ico-s" /> {tx('PDF bilet', 'PDF ticket')}
            </button>
            <button
              type="button"
              className="btn btn-line"
              onClick={() => {
                icsFor(o);
                toast(tx('Takvim dosyası indirildi', 'Calendar file downloaded'));
              }}
            >
              <Ico n="cal" cls="ico-s" /> {tx('Takvime ekle', 'Add to calendar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Printable A4 — every ticket of the order on one sheet; "Save as PDF" from the print dialog (client-side). */
export function TicketPdf() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const k = params.get('k');
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const { order: o, error } = useOrder(id, k);
  if (error) return <ErrorView msg={tx('Bilet bulunamadı.', 'Ticket not found.')} />;
  if (!o) return <Loading />;
  return (
    <div className="scroll" style={{ background: 'var(--paper-2)' }}>
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 28px', background: 'var(--card)', borderBottom: '1px solid var(--rule)', position: 'sticky', top: 0, zIndex: 3, flexWrap: 'wrap' }}>
        <button type="button" className="icon-btn" onClick={() => nav(`/bilet/${o.id}${k ? `?k=${k}` : ''}`)} aria-label={tx('E-bilete dön', 'Back to e-ticket')}>
          <Ico n="back" />
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="d-s">{tx('PDF bilet', 'PDF ticket')}</div>
          <div className="t-s muted">{tx('A4 · tüm biletler tek sayfada · siyah-beyaz yazıcıda da okunur', 'A4 · all tickets on one page · readable on black-and-white printers')}</div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          <Ico n="print" cls="ico-s" /> {tx('Yazdır / PDF olarak kaydet', 'Print / save as PDF')}
        </button>
      </div>
      <div className="a4-wrap" style={{ display: 'flex', justifyContent: 'center', padding: '32px 0 60px', overflowX: 'auto' }}>
        <A4 o={o} lang={lang} />
      </div>
    </div>
  );
}

function A4({ o, lang }: { o: OrderOut; lang: 'tr' | 'en' }) {
  const en = lang === 'en';
  const e = o.event;
  const T = (tr: string, enS: string) => (en ? enS : tr);
  return (
    <div className="a4">
      <div className="head">
        <Logo size={44} />
        <div style={{ textAlign: 'right' }}>
          <div className="cap muted">{T('Sipariş', 'Order')}</div>
          <div className="mono" style={{ fontSize: 15, fontWeight: 600 }}>
            {o.no}
          </div>
          <div className="t-s muted">
            {o.buyer.name} · {o.buyer.email}
          </div>
        </div>
      </div>
      {o.tickets.map((t, i) => (
        <div className="tk" key={t.id}>
          <div className="l">
            <div className="cap muted">
              {e.kind} · {dayLong(o.session.startsAt, lang)} · {hhmm(o.session.startsAt)}
            </div>
            <div className="d-l" style={{ fontSize: 44 }}>
              {e.title}
            </div>
            <div className="t-s" style={{ color: 'var(--ink-2)' }}>
              {en ? e.creditEn || e.credit : e.credit}
              <br />
              {o.venue.name} · {o.venue.addr}
            </div>
            <div className="kv">
              <div>
                <div className="k">{T('Bölüm', 'Section')}</div>
                <div className="v">{t.single ? 'Loca' : t.sec}</div>
              </div>
              <div>
                <div className="k">{T('Sıra', 'Row')}</div>
                <div className="v">{t.row}</div>
              </div>
              <div>
                <div className="k">{T('Koltuk', 'Seat')}</div>
                <div className="v">{t.num}</div>
              </div>
              <div>
                <div className="k">{T('Kapı', 'Door')}</div>
                <div className="v">{t.gate}</div>
              </div>
            </div>
            <div className="t-s muted">
              {en ? TIERS[t.tier].en : TIERS[t.tier].tr} · {tl(t.priceKurus, lang)} + {tl(FEE_KURUS, lang)} {T('hizmet bedeli', 'service fee')} · {T('Kapılar', 'Doors')} {e.doors} · {en ? e.ageEn : e.age}
            </div>
          </div>
          <div className="r">
            {t.qr && <Qr text={t.qr} px={150} />}
            <span className="mono" style={{ fontSize: 11, letterSpacing: '.08em' }}>
              {t.id}
            </span>
            <span className="cap muted" style={{ fontSize: 9.5 }}>
              {T('Bilet', 'Ticket')} {i + 1} / {o.tickets.length}
            </span>
          </div>
        </div>
      ))}
      <div className="terms">
        <p>
          <b>{T('Giriş.', 'Entry.')}</b> {T('Bu belgedeki her QR kod tek bir kişinin tek girişi içindir. İlk okutmadan sonra aynı kod tekrar kullanılamaz.', 'Each QR code admits one person once. After the first scan the same code can’t be used again.')}
        </p>
        <p>
          <b>{T('Geç giriş.', 'Late entry.')}</b> {T('Gösteri başladıktan sonra salona yalnızca ara verildiğinde, görevli eşliğinde girilebilir.', 'Once the show has started, latecomers are seated at the interval by an usher.')}
        </p>
        <p>
          <b>{T('Erişilebilirlik.', 'Accessibility.')}</b> {T('Asansör ve tekerlekli sandalye alanı için Kapı B’yi kullanın. Yardım: +90 216 000 00 00.', 'Use door B for the lift and wheelchair spaces. Help: +90 216 000 00 00.')}
        </p>
        <p>
          <b>{T('İade.', 'Refunds.')}</b> {T('Etkinlik iptal edilirse ücret, ödemenin yapıldığı karta 14 gün içinde iade edilir. Seans değişikliğinde biletler yeni seansa aktarılır.', 'If the event is cancelled you’re refunded to the paying card within 14 days. If the session moves, tickets move with it.')}
        </p>
        <p>
          <b>{T('Kayıt.', 'Recording.')}</b> {T('Gösteri sırasında ses ve görüntü kaydı alınamaz.', 'No audio or video recording during the show.')}
        </p>
        <p>
          <b>{T('Yaş sınırı.', 'Age limit.')}</b> {T(`${e.age} sınırı uygulanır; kapıda kimlik sorulabilir.`, `${e.ageEn} applies; ID may be checked at the door.`)}
        </p>
      </div>
      <div className="foot">
        <span>Gişe Bilet A.Ş. · gise.melihkizmaz.dev</span>
        <span>
          {T('Yazdırma', 'Printed')}: {new Date().toLocaleDateString(en ? 'en-GB' : 'tr-TR')}
        </span>
      </div>
    </div>
  );
}
